/**
 * Browser Notifications Service
 *
 * Per UX spec (lines 1386-1442):
 * - Permission request flow
 * - Notification events (party invite, vote, game starting)
 * - Settings toggle per notification type
 */

import { NOTIFICATION_DISMISS_MS } from '@danda/shared';

export type NotificationType = 'party_invite' | 'vote' | 'game_starting' | 'message' | 'system';

interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
}

class NotificationService {
  private permission: NotificationPermission | null = null;
  private permissionRequestFailed = false;
  private enabledTypes: Set<NotificationType> = new Set([
    'party_invite',
    'vote',
    'game_starting',
    'message',
  ]);

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    // Don't retry if a previous request threw an error (browser bug/security policy)
    if (this.permissionRequestFailed) {
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permission = { granted: true, denied: false, default: false };
      return true;
    }

    if (Notification.permission === 'denied') {
      this.permission = { granted: false, denied: true, default: false };
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      this.permission = {
        granted,
        denied: permission === 'denied',
        default: permission === 'default',
      };

      return granted;
    } catch (error) {
      console.error('Notification permission request failed', error);
      this.permissionRequestFailed = true;
      this.permission = { granted: false, denied: true, default: false };
      return false;
    }
  }

  /**
   * Check if notifications are enabled for a specific type
   */
  isEnabled(type: NotificationType): boolean {
    if (!this.permission?.granted) return false;
    return this.enabledTypes.has(type);
  }

  /**
   * Enable/disable notifications for a specific type
   */
  setEnabled(type: NotificationType, enabled: boolean): void {
    if (enabled) {
      this.enabledTypes.add(type);
    } else {
      this.enabledTypes.delete(type);
    }
    // Persist to localStorage
    try {
      localStorage.setItem('notification-types', JSON.stringify(Array.from(this.enabledTypes)));
    } catch (error) {
      console.error('Failed to save notification preferences', error);
    }
  }

  /**
   * Send a browser notification
   */
  async notify(
    title: string,
    options: NotificationOptions & { type?: NotificationType } = {}
  ): Promise<void> {
    if (!('Notification' in window)) {
      return;
    }

    const { type, ...notificationOptions } = options;

    // Check if this notification type is enabled
    if (type && !this.isEnabled(type)) {
      return;
    }

    // Check permission
    if (Notification.permission !== 'granted') {
      const granted = await this.requestPermission();
      if (!granted) {
        return;
      }
    }

    try {
      // Create and show notification
      const notification = new Notification(title, {
        icon: '/vite.svg', // TODO: Use proper app icon
        badge: '/vite.svg',
        tag: type || 'default',
        requireInteraction: type === 'party_invite' || type === 'vote',
        ...notificationOptions,
      });

      // Auto-close after 5 seconds (unless requireInteraction is true)
      if (!notificationOptions.requireInteraction) {
        setTimeout(() => notification.close(), NOTIFICATION_DISMISS_MS);
      }

      // Handle click
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Failed to display notification', error);
    }
  }

  /**
   * Load notification preferences from localStorage
   */
  loadPreferences(): void {
    try {
      const stored = localStorage.getItem('notification-types');
      if (stored) {
        this.enabledTypes = new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load notification preferences', error);
    }
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission | null {
    if (!('Notification' in window)) {
      return null;
    }

    return {
      granted: Notification.permission === 'granted',
      denied: Notification.permission === 'denied',
      default: Notification.permission === 'default',
    };
  }

  /**
   * Get all enabled notification types (for settings page)
   */
  getEnabledTypes(): Set<NotificationType> {
    return new Set(this.enabledTypes);
  }
}

export const notificationService = new NotificationService();

// Load preferences on initialization
if (typeof window !== 'undefined') {
  notificationService.loadPreferences();
}
